/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { classes, isIOSMobileDevice } from '@adobe-commerce/elsie/lib';
import { JSX } from 'preact/compat';
import { Field, Icon, Input, InputProps } from '@adobe-commerce/elsie/components';
import { Date } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';

import '@adobe-commerce/elsie/components/InputDate/InputDate.css';
import { useCallback } from 'react';

export interface InputDateProps extends Omit<InputProps, 'error' | 'value'> {
  label?: string;
  name?: string;
  error?: string;
  value?: Date;
}

export const InputDate: FunctionComponent<InputDateProps> = ({
  name = '',
  error,
  value,
  label,
  onChange,
  onBlur,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value?.toString() ?? '');
  const iosInputRef = useRef<HTMLInputElement>(null);
  const dictionary = useText({
    picker: 'Dropin.InputDate.picker',
  });

  const handleOnFocus: JSX.MouseEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      event.currentTarget.setAttribute('type', 'date');

      if (!isIOSMobileDevice()) return;

      iosInputRef.current.focus();
    },
    []
  );

  const handleOnClick: JSX.MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      const input = event.currentTarget.parentElement?.querySelector('input');
      input?.focus();
      input?.showPicker();
    },
    []
  );

  const handleOnBlur: JSX.MouseEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      event.currentTarget.setAttribute('type', 'text');
      onBlur?.(event);
    },
    [onBlur]
  );

  const handleInputChange: JSX.MouseEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        setInternalValue(event.target.value);
        onChange?.(event);
      },
      [onChange]
    );

  return (
    <div className={classes(['dropin-input-date'])}>
      {/* This simple input is needed only for iOS devices to trigger the date picker. */}
      {isIOSMobileDevice() ? (
        <input
          ref={iosInputRef}
          data-testid="inputDateIos"
          className="dropin-input-date__input--ios"
          type="date"
          onChange={handleInputChange}
        />
      ) : null}
      <Field error={error}>
        <Input
          data-testid="input-date"
          error={!!error}
          name={name}
          value={internalValue}
          placeholder={label}
          floatingLabel={label}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onChange={handleInputChange}
          aria-labelledby={label}
          className="dropin-input-date__input"
          {...props}
        />
      </Field>

      <button
        type="button"
        data-testid="dropin-input-date__icon"
        className="dropin-input-date__icon"
        aria-label={dictionary.picker}
        onClick={handleOnClick}
      >
        <Icon source={Date} size="24" />
      </button>
    </div>
  );
};
